/**
 * describeSendError — one place that turns a send/reply failure into the
 * message the user actually sees.
 *
 * Extracted because PersonalThreadView, EmailDetailPage and the compose pages
 * each carried their own copy of the same if-ladder, and the copies had
 * drifted (ComposePage said "Failed to send email", EmailDetailPage said
 * "Failed to send reply" for the same 502). The backend's error contract for
 * POST /inbox/personal/messages/{comm_id}/reply:
 *
 *   404 'message_not_found'
 *   401 {code:'reconnect_required', mailbox}
 *   422 'reply_mailbox_unknown' | 'recipient_required'
 *   400 {code:'invalid_recipient'|'invalid_request', provider_error}
 *   429 'throttled'
 *   504 'send_timeout'
 *   502 'send_failed'
 *
 * `detail` can be a bare string OR `{ code, mailbox }` — the 401 reconnect
 * case is the one that is always an object, so `typeof detail === 'string'`
 * is the discriminator, not `data.detail` truthiness.
 */
export function describeSendError(err) {
    const status = err?.response?.status;
    const data = err?.response?.data;
    const detail = typeof data?.detail === 'string' ? data.detail : data?.detail?.code;
    if (err?.mailboxStatusUnknown) return "We couldn't check your mailbox connection. Please retry in a moment.";
    // #659 — a send that refused the CRM log-only fallback (useSendEmail
    // `allowLogFallback: false`): nothing was sent and nothing was logged.
    if (err?.noDeliveryRoute) return 'Not sent: no connected mailbox can deliver it. Reconnect your mailbox and try again.';
    if (status === 400 && (detail === 'invalid_recipient' || detail === 'invalid_request'))
        return 'The mail server rejected a recipient address. Check the To / Cc / Bcc fields and try again.';
    if (status === 502 || status === 503) return 'The mail server is temporarily unavailable. Please wait a moment and try again.';
    if (status === 504 || detail === 'send_timeout') return 'The email server took too long to respond. Please try again.';
    if (detail === 'reconnect_required') {
        const mailbox = data?.detail?.mailbox;
        return mailbox ? `Reconnect ${mailbox} to reply from it.` : 'Your mailbox connection has expired. Please re-authorize in Settings.';
    }
    if (detail === 'from_address_denied') return 'You may not send from that address';
    if (detail === 'recipient_required') return 'Please add at least one recipient';
    if (detail === 'send_failed') return 'Failed to send reply. The mail server may be busy — please try again in a moment.';
    if (typeof data === 'string' && /<html/i.test(data)) return 'The server returned an error page instead of a reply status. Please try again.';
    return detail || 'Failed to send reply';
}
